/**
 * services/buildSymbolContext.ts
 *
 * CANONICAL symbol context service for MER Algo Footprints v4.3+
 *
 * PIPELINE:
 *   buildSymbolContext()       → SymbolContext   (canonical deterministic state)
 *   buildSymbolContextForAI()  → MarketContext   (pure adapter for AI pipeline)
 *
 * buildSymbolContextForAI() is the ONLY permitted way to create AI context.
 * It calls buildSymbolContext() then wraps into MarketContext via contextBuilder.
 * No component may assemble a MarketContext manually from raw engine outputs.
 *
 * Previously, 7 separate files each independently rebuilt the full engine
 * chain (gamma → zones → SMC → regime → traps → setups). This caused:
 *   - 60+ duplicate engine call sites
 *   - Inconsistent outputs across panels
 *   - PostureBanner running scoreRegime twice per render
 *   - Performance drag on large watchlists
 *
 * This service is the SINGLE SOURCE OF TRUTH for symbol context.
 * All consumers (Dashboard, ThesisPanel, AIAnalystPanel, PostureBanner,
 * WatchlistSidebar, useAIOrchestration, panels) should call this.
 *
 * Usage:
 *   const ctx = buildSymbolContext(symbol, symbolData[symbol], settings, weights);
 *   if (!ctx) return null; // no data yet
 *
 * The result is fully typed and contains everything downstream consumers need.
 */

import { logDiagnostic } from '../../../diagnostics/diagnosticsStore';
import { evaluateStateTransition, StateTransitionResult } from '../engines/stateTransition';
import { withEffectiveConfidenceBand } from '../../../shared/confidenceBand';
import { interpretTraps, TrapEngineOutput } from '../engines/traps/trapInterpreter';
import { buildMarketContext }   from '../../../ai/contextBuilder';
import { inferGamma }          from '../../../engines/gammaEngine';
import { buildLiquidityZones } from '../../../engines/liquidityEngine';
import { getLivePrintClusters } from '../../ai/prompts/promptBuilder';
import { analyzeSMC }          from '../../../engines/smcEngine';
import { analyzeTrend }        from '../../../engines/trendEngine';
import { scoreRegime }         from '../../../engines/regimeEngine';
import { detectTraps }         from '../../../engines/trapEngine';
// Backlog #6: migrated from v1 setupEngine -> setup2.
// setup2 emits explicit NVS sentinel (TradeSetup with type='[NO_VALID_SETUP]',
// confidence=0) which the divergence layer and ThesisPanel rely on.
import { generateRichSetups, richSetupsToTradeSetups, type RichSetup } from '../engines/setup2';
// Phase 6.3A — Gamma influence post-pass (controlled, bounded, feature-flagged)
import { applyGammaInfluencePass }    from '../engines/gamma/gammaInfluence';
import { deriveGammaInfluenceInput }  from '../engines/gamma/gammaDerivation';
// Phase 6.8 — Runtime promotion orchestration (replaces static GAMMA_INFLUENCE_FLAGS reads)
import { resolvePromotionContext }    from '../engines/gamma/gammaPromotionOrchestrator';
// Phase 6.5 — Attribution capture (additive, does not change scoring behavior)
import { buildAttributionRecord }     from '../engines/gamma/gammaAttribution';
import { appendAttribution }          from '../engines/gamma/gammaAttributionStore';
// Phase 6.5.2 — Explicit evaluation cycle identity for attribution correlation
import { getEvaluationCycleId }       from '../engines/gamma/gammaEvaluationCycle';
import {
  Candle, SessionMeta, OptionRow, GammaResult, LiquidityZone,
  SMCResult, RegimeResult, TrapSignal, TradeSetup, AppSettings,
  ScoringWeights, DataState,
  TrendResult,
  ProviderConfidence,
} from '../../../types';

// ─── Output contract ──────────────────────────────────────────────────────────

export interface SymbolContext {
  // Input
  symbol:   string;
  candles:  Candle[];
  options:  OptionRow[];
  meta:     Partial<SessionMeta>;
  interval: string;
  // P3-D4 IMPL-3 (criterion H, D-P3T-4/-15): the governed provider-evidence
  // confidence for the market-data observation that produced these candles,
  // carried through unchanged. RECEIPT ONLY — no engine may branch on it, and
  // no existing confidence model is derived from or altered by it. Optional
  // because legitimate absence is part of the banked runtime semantics, and
  // absence stays distinct from the governed `UNKNOWN` state.
  providerConfidence?: ProviderConfidence;

  // Engine outputs — all nullable if data insufficient
  gamma:   GammaResult | null;
  zones:   LiquidityZone[];
  smc:     SMCResult | null;
  trend:   TrendResult | null;
  regime:  RegimeResult | null;
  traps:   TrapSignal[];
  setups:  TradeSetup[];
  // Phase 4A: canonical setup2 output retained alongside the TradeSetup projection.
  // setups (above) stays the legacy contract; these expose the un-lossy RichSetup canonical.
  richSetups?:       RichSetup[];
  primaryRichSetup?: RichSetup | null;

  // Derived convenience fields
  curPrice:    number;
  curVWAP:     number | null;
  hasOptions:  boolean;
  candleCount: number;
  // Level hierarchy: nearest actionable vs higher structural reference
  activeDecisionLevel: { price: number; label: string; distPct: number } | null;
  structuralLevel:     { price: number; label: string; distPct: number } | null;

  // Data quality
  dataQuality: DataQualityAssessment;
  // v7.1H: State Transition Layer — explicit market state + actionability
  stateTransition: StateTransitionResult;
  /** v8.2.0: Enhanced trap interpretation (gamma + liquidity + SMC). */
  trapInterpretation: TrapEngineOutput | null;
  // v5.5: deterministic actionability gate
  setupActionable:     boolean;
  // v5.5: canonical setup split — display vs actionable (patch 10/10)
  topDisplaySetup:     TradeSetup | null;
  bestActionableSetup: TradeSetup | null;
  setupTruth:          import('../../../types').SetupTruth;
}

export interface DataQualityAssessment {
  hasCandles:      boolean;
  hasSufficientBars: boolean;  // ≥ 30 candles for meaningful analysis
  hasOptions:      boolean;
  hasGreeks:       boolean;    // real per-strike Greeks (TradeStation)
  hasRealData:     boolean;    // API vs sample/CSV
  completeness:    number;     // 0–100 score
  warnings:        string[];
}

// ─── Minimum bars required ────────────────────────────────────────────────────

const MIN_BARS_BASIC    = 15;   // minimum for any analysis
const MIN_BARS_STANDARD = 30;   // recommended for reliable signals
const MIN_BARS_FULL     = 60;   // full SMC swing detection

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildSymbolContext(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): SymbolContext | null {
  if (!data?.candles?.length || data.candles.length < MIN_BARS_BASIC) return null;

  const candles  = data.candles;
  const options  = data.options  ?? [];
  const meta     = data.meta     ?? {};
  const interval = (data.interval ?? settings.defaultInterval) as string;

  // ── Engine chain (single canonical run) ──────────────────────────────────
  const gamma  = options.length ? inferGamma(options, candles, meta) : null;
  // Phase 2 v1 (liquidity multi-source): read off-exchange print clusters
  // from the institutional flow sidecar. Null when the flow runtime hasn't
  // populated yet (initial load, markets closed, missing API key) -- in
  // which case liquidityEngine returns to pre-v1 behavior. Symbol must be
  // passed explicitly because the sidecar is keyed by uppercased ticker.
  const printClustersSnapshot = getLivePrintClusters(symbol);
  const printClusters = printClustersSnapshot?.clusters ?? null;
  const zones  = buildLiquidityZones(candles, options.length ? options : null, meta, settings, printClusters);
  const smc    = analyzeSMC(candles, settings);
  const trend  = analyzeTrend(candles);
  // Phase 2 v1 (regime modifiers): compute traps BEFORE regime so the
  // regime modifier detector can see active trap signals in the same
  // pass. detectTraps depends only on candles+zones+settings (no regime
  // dependency), so the swap is safe.
  const traps  = detectTraps(candles, zones, settings);
  const regime = scoreRegime(candles, gamma, zones, weights, settings, smc, trend, traps);

  // ── Quality-gated setup generation ────────────────────────────────────────
  // Do not attempt setup generation without enough bars for reliable SMC signals.
  // Low-quality context should produce fewer, more conservative setups naturally
  // because the underlying engines have weaker evidence — enforce a minimum floor here.
  const enoughBarsForSetups = candles.length >= MIN_BARS_STANDARD;
  // Backlog #6: setup2 takes a structured input record (instead of positional
  // args) and returns RichSetup[]. The richSetupsToTradeSetups adapter converts
  // back to the legacy TradeSetup[] shape so downstream code (gamma influence
  // pass, scoring, symCtx.setups consumers) sees the same contract -- with the
  // critical difference that NVS is now an explicit entry rather than an empty
  // array. ThesisPanel and computeDivergence.ts are already NVS-aware.
  const curPriceForSetup = candles[candles.length - 1].close;
  // Phase 4A: retain the canonical RichSetup[] instead of discarding it inside the
  // adapter call. richSetups is the un-lossy canonical (readiness_score, audit,
  // input_hash, breakdowns, etc.); rawSetups remains the legacy TradeSetup[] projection
  // all existing downstream code consumes. Behavior unchanged: the adapter receives
  // exactly what it received before, and the skip-branch still yields [] without calling it.
  const richSetups: RichSetup[] = (regime && enoughBarsForSetups)
    ? generateRichSetups({
        symbol,
        timeframe: interval,
        candles,
        smc,
        regime,
        traps,
        zones,
        trend,
        gamma,
        curPrice: curPriceForSetup,
      })
    : [];
  const rawSetups = (regime && enoughBarsForSetups)
    ? richSetupsToTradeSetups(richSetups)
    : [];

  // ── Phase 6.3A / 6.8: Gamma influence post-pass with promotion routing ────
  // Derives GammaInfluenceInput via canonical helper (gammaDerivation.ts).
  // Phase 6.8: flags come from the runtime promotion orchestrator, not the
  // static GAMMA_INFLUENCE_FLAGS constant. When no candidate is active (the
  // default), the orchestrator returns GAMMA_INFLUENCE_FLAGS unchanged.
  // When a shadow candidate is active, effectiveFlags = baseline, and a second
  // shadow pass is captured for observation without affecting live setups.
  const spot = candles.length ? candles[candles.length - 1].close : 0;
  // v8.2.0: Enhanced trap interpretation (SMC + regime context; gamma injected externally).
  const trapInterpretation: TrapEngineOutput | null = (() => {
    try {
      return interpretTraps({ symbol, spot, traps, smc, regime, gammaInterp: null, liqOutput: null });
    } catch { return null; }
  })();
  const gammaInfluenceInput = deriveGammaInfluenceInput(options, symbol, spot);

  // Get top setup type for scope matching (limited rollout requires this)
  const _topSetupType = rawSetups[0]?.type ?? 'unknown';
  const promotionCtx  = resolvePromotionContext(symbol, _topSetupType);
  const effectiveFlags = promotionCtx.effectiveFlags;

  const { setups, audits: gammaAudits, applied: gammaApplied } =
    applyGammaInfluencePass(rawSetups, gammaInfluenceInput, symbol, effectiveFlags);

  if (effectiveFlags.gammaAuditLoggingEnabled && gammaAudits.length > 0) {
    logDiagnostic(
      'info',
      'signal_scored',
      `[Gamma Influence] symbol=${symbol} profile=${promotionCtx.profileId} mode=${promotionCtx.rolloutDecision} enabled=${effectiveFlags.gammaScoringEnabled} applied=${gammaApplied}/${gammaAudits.length}`,
      symbol,
      JSON.stringify({ applied: gammaApplied, audits: gammaAudits, profileId: promotionCtx.profileId }),
    );
  }

  // Phase 6.5 — Attribution capture: live pass records (with promotion metadata).
  if (effectiveFlags.gammaAuditLoggingEnabled && gammaAudits.length > 0) {
    const now             = Date.now();
    const evaluationCycleId = getEvaluationCycleId(symbol);
    for (const audit of gammaAudits) {
      appendAttribution(buildAttributionRecord({
        capturedAt:         now,
        symbol,
        setupType:          audit.setupType,
        setupDir:           audit.setupDir,
        baseConfidence:     audit.priorConfidence,
        baseRankScore:      null,
        confidenceAudit:    audit,
        rankingAudit:       null,
        evaluationCycleId,
        promotionProfileId: promotionCtx.profileId,
        rolloutDecision:    promotionCtx.rolloutDecision,
        isShadowOnly:       false,   // live pass is never shadow-only
      }));
    }
  }

  // Phase 6.8 — Shadow confidence pass: when a shadow candidate exists,
  // run a parallel pass with candidate flags for observation only.
  // This DOES NOT affect setups, confidence, or any live scoring output.
  if (promotionCtx.isShadow && promotionCtx.candidateFlags && gammaInfluenceInput) {
    const { audits: shadowAudits } =
      applyGammaInfluencePass(rawSetups, gammaInfluenceInput, symbol, promotionCtx.candidateFlags);
    if (promotionCtx.candidateFlags.gammaAuditLoggingEnabled && shadowAudits.length > 0) {
      const now             = Date.now();
      const evaluationCycleId = getEvaluationCycleId(symbol);
      for (const audit of shadowAudits) {
        appendAttribution(buildAttributionRecord({
          capturedAt:         now,
          symbol,
          setupType:          audit.setupType,
          setupDir:           audit.setupDir,
          baseConfidence:     audit.priorConfidence,
          baseRankScore:      null,
          confidenceAudit:    audit,
          rankingAudit:       null,
          evaluationCycleId,
          promotionProfileId: promotionCtx.profileId,
          rolloutDecision:    'shadow',
          isShadowOnly:       true,   // shadow observation only — not live
        }));
      }
    }
  }

  // ── Derived convenience fields ─────────────────────────────────────────
  const curPrice = candles[candles.length - 1].close;
  const curVWAP  = regime?.curVWAP ?? null;

  // ── Data quality assessment ────────────────────────────────────────────
  const dataQuality = assessDataQuality(candles, options, data.source ?? 'unknown', meta);

  // ── A2: Active decision level vs structural level ─────────────────────────
  // activeDecisionLevel: nearest significant zone to current price (actionable)
  // structuralLevel: highest-sig zone if materially different from active level
  const _sortedZones = zones
    .filter(z => z.distPct < 2.0)
    .sort((a, b) => a.distPct - b.distPct);
  const _nearestAny = _sortedZones[0] ?? null;
  const _nearestHigh = _sortedZones.find(z => z.sig === 'High') ?? null;
  // Active = nearest zone (High or not); structural = nearest High-sig if materially farther
  const activeDecisionLevel = _nearestAny
    ? { price: _nearestAny.price, label: _nearestAny.label, distPct: _nearestAny.distPct }
    : null;
  const structuralLevel = (_nearestHigh && (!_nearestAny ||
    Math.abs(_nearestHigh.price - _nearestAny.price) / _nearestAny.price > 0.003))
    ? { price: _nearestHigh.price, label: _nearestHigh.label, distPct: _nearestHigh.distPct }
    : null; // null = same as active, no duplication needed

  // ── v5.5 Patch 10/10: Canonical setup split ───────────────────────────────
  // topDisplaySetup: highest-confidence setup shown in UI (may be developing/countertrend)
  // bestActionableSetup: first setup passing the full maturity+alignment gate
  // setupActionable: true only when at least one setup is genuinely actionable
  const topDisplaySetup = setups[0] ?? null;

  const bestActionableSetup =
    // Tier 1: fully actionable + aligned or countertrend-confirmed
    setups.find(s =>
      s.maturity === 'actionable' &&
      (s.alignment === 'aligned' || s.alignment === 'countertrend-confirmed')
    ) ??
    // Tier 2: confirmed + aligned (but not yet fully actionable)
    setups.find(s =>
      s.maturity === 'confirmed' &&
      s.alignment === 'aligned'
    ) ??
    null;

  const setupActionable = Boolean(bestActionableSetup);

  // Canonical truth object — prefer bestActionableSetup, fall back to topDisplaySetup
  const _truthSource = bestActionableSetup ?? topDisplaySetup;
  const setupTruth: import('../../../types').SetupTruth = {
    displaySetupType:        topDisplaySetup?.type ?? null,
    actionableSetupType:     bestActionableSetup?.type ?? null,
    actionableSetupDir:      (bestActionableSetup?.dir === 'long' || bestActionableSetup?.dir === 'short')
                               ? bestActionableSetup.dir : null,
    maturity:                (_truthSource?.maturity ?? null) as import('../../../types').SetupMaturity | null,
    alignment:               (_truthSource?.alignment ?? null) as import('../../../types').SetupAlignment | null,
    countertrend:            _truthSource?.countertrend ?? false,
    stateTransitionConfirmed: _truthSource?.stateTransitionConfirmed ?? false,
    confirmationMissing:     _truthSource?.confirmationMissing ?? [],
  };

  // ── v7.1H: State Transition Layer ────────────────────────────────────────────
  // Converts analysis into an explicit finite market state + actionability.
  // Sits between engine outputs and UI — strictly additive.
  const stateTransitionBase = evaluateStateTransition(
    smc, regime, traps, zones, trend, gamma, setups,
    candles.length, curPrice,
    setupActionable,
    setups[0]?.confidence ?? 0,
  );

  // P3 confidence penalties (D-P3AU): the narrowest composition point — the
  // only place where the structural result and the governed provider evidence
  // are both in scope. The engine above is left provider-blind, and
  // `confidence` itself is carried through untouched.
  const stateTransition: StateTransitionResult =
    withEffectiveConfidenceBand(stateTransitionBase, data.providerConfidence);

  const result: SymbolContext = {
    symbol,
    candles,
    options,
    meta,
    interval,
    // Carried through verbatim, never read: presence is the criterion-H
    // behaviour and behavioural consumption is out of scope (D-P3T-15).
    ...(data.providerConfidence !== undefined
      ? { providerConfidence: data.providerConfidence }
      : {}),
    gamma,
    zones,
    smc,
    trend,
    regime,
    traps,
    setups,
    richSetups,
    primaryRichSetup: richSetups[0] ?? null,
    curPrice,
    curVWAP,
    hasOptions:      options.length > 0,
    candleCount:     candles.length,
    activeDecisionLevel,
    structuralLevel,
    dataQuality,
    setupActionable,
    topDisplaySetup,
    bestActionableSetup:  bestActionableSetup ?? null,
    setupTruth,
    stateTransition,
    trapInterpretation,
  };

  logDiagnostic('info', 'context_built', `Context built: ${symbol} (${candles.length} bars)`, symbol);
  return result;}

// ─── Build context for AI pipeline ───────────────────────────────────────────
// Returns the MarketContext object needed by promptBuilder / aiOrchestrator

export function buildSymbolContextForAI(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): ReturnType<typeof buildMarketContext> | null {
  const ctx = buildSymbolContext(symbol, data, settings, weights);
  if (!ctx) return null;

  return buildMarketContext(
    symbol,
    ctx.candles,
    ctx.meta,
    ctx.regime,
    ctx.gamma,
    ctx.smc,
    ctx.traps,
    ctx.zones,
    ctx.setups,
    settings,
    ctx.interval,
    ctx.hasOptions   // Defect 4: true source — not inferred from gamma
  );
}

// ─── Lightweight watchlist summary (for sidebar rows) ────────────────────────
// Deliberately cheaper than full context — omits traps/setups for performance

export interface WatchlistRowSummary {
  symbol:       string;
  curPrice:     number | null;
  chgPct:       number | null;
  regimeLabel:  string;
  regimeColor:  string;
  smcBias:      'bullish' | 'bearish' | 'neutral';
  rvol:         number | null;
  dataQuality:  DataQualityAssessment;
}

export function buildWatchlistRowSummary(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): WatchlistRowSummary {
  const noData: WatchlistRowSummary = {
    symbol, curPrice: null, chgPct: null,
    regimeLabel: '—', regimeColor: 'var(--blue-dim)',
    smcBias: 'neutral', rvol: null,
    dataQuality: assessDataQuality([], [], 'unknown', {}),
  };

  if (!data?.candles?.length) return noData;

  const candles = data.candles;
  const curPrice  = candles[candles.length - 1].close;
  const prevPrice = candles[0].open;
  const chgPct    = prevPrice > 0 ? ((curPrice - prevPrice) / prevPrice) * 100 : null;

  // Derive from canonical buildSymbolContext — no independent engine runs
  const ctx = buildSymbolContext(symbol, data, settings, weights);

  const regime     = ctx?.regime ?? null;
  const smc        = ctx?.smc    ?? null;
  const options    = ctx?.options ?? [];
  const meta       = ctx?.meta   ?? {};
  const regimeWord = regime?.label?.split(' ')[0] ?? '—';
  const regimeLabel = regimeWord === 'Mean' ? 'MR' : regimeWord === 'Breakout' ? 'BO'
    : regimeWord === 'Trend' ? 'TR' : regimeWord === '—' ? '—' : 'NT';
  const REGIME_COLORS: Record<string, string> = {
    MR: 'var(--green)', BO: 'var(--blue)', TR: 'var(--purple)', NT: 'var(--amber)',
  };

  // rvol from regime (already computed in context)
  const rvol: number | null = regime?.rvol ?? null;

  return {
    symbol, curPrice, chgPct,
    regimeLabel,
    regimeColor: REGIME_COLORS[regimeLabel] ?? 'var(--blue-dim)',
    smcBias:     smc?.bias ?? 'neutral',
    rvol,
    dataQuality: assessDataQuality(candles, options, data.source ?? 'unknown', meta),
  };
}

// ─── Data quality assessment ──────────────────────────────────────────────────

export function assessDataQuality(
  candles:  Candle[],
  options:  OptionRow[],
  source:   string,
  meta:     Partial<SessionMeta>
): DataQualityAssessment {
  const warnings: string[] = [];
  let completeness = 0;

  const hasCandles         = candles.length > 0;
  const hasSufficientBars  = candles.length >= MIN_BARS_STANDARD;
  const hasOptions         = options.length > 0;
  const hasGreeks          = options.some(o => o.gamma !== null && o.gamma !== undefined);
  const hasRealData        = source === 'api';

  if (hasCandles)        completeness += 40;
  if (hasSufficientBars) completeness += 20;
  if (hasOptions)        completeness += 20;
  if (hasGreeks)         completeness += 15;
  if (hasRealData)       completeness += 5;

  if (!hasCandles)          warnings.push('No candle data');
  else if (!hasSufficientBars) warnings.push(`Only ${candles.length} bars — ${MIN_BARS_STANDARD}+ recommended`);
  if (candles.length < MIN_BARS_FULL) warnings.push('Limited swing detection below 60 bars');
  if (!hasOptions)          warnings.push('No options data — gamma analysis unavailable');
  else if (!hasGreeks)      warnings.push('Options OI only — real Greeks not available');
  if (!hasRealData)         warnings.push('Sample/CSV data — not live market data');

  return {
    hasCandles,
    hasSufficientBars,
    hasOptions,
    hasGreeks,
    hasRealData,
    completeness: Math.min(100, completeness),
    warnings,
  };
}
